// @flow
import { useState, useMemo } from "react";
import { useLocation, useRouteMatch } from "react-router";
import Href from "../models/href/Href";
import { useModularUIBasic } from "./useModularUIBasic";

import type { HookOptions } from "./useModularUIBasic";
import GroupingPanelModel from "../models/panels/GroupingPanelModel";
import ListModel from "../models/list/ListModel";
import DetailModel from "../models/detail/DetailModel";
import CaseSearchModel from "../models/search/CaseSearchModel";

const useUrl = (href?: string | Href, fromRoute: boolean = false): Href => {
  const [previousUrl, setPreviousUrl] = useState(null);
  const [previousExact, setPreviousExact] = useState(false);

  const match = useRouteMatch();
  const location = useLocation();

  return useMemo(() => {
    if (href == null && previousUrl == null) {
      return new Href("");
    }

    const url = href instanceof Href ? href : new Href(href);

    if (!fromRoute) {
      return url;
    }

    if (match.isExact) {
      url.addParametersFromString(location.search);
    }

    // Check equality to prevent unnecessary state updates
    if (url.equalsWithParameters(previousUrl)) {
      return previousUrl || url;
    }

    const shouldUpdate =
      match.isExact === true ||
      previousExact === match.isExact ||
      !previousUrl?.startsWith(match.url);

    if (shouldUpdate) {
      setPreviousUrl(url);
      setPreviousExact(match.isExact);
      return url;
    }

    return previousUrl || new Href("");
  }, [
    href,
    previousUrl,
    fromRoute,
    match.isExact,
    match.url,
    previousExact,
    location.search,
  ]);
};

/**
 */
export const usePanel = (
  href?: string | Href,
  options?: { ...HookOptions, fromRoute?: boolean },
): ListModel | GroupingPanelModel | DetailModel | null => {
  const { fromRoute, ...hookOptions } = options || { fromRoute: false };

  const url = useUrl(href, fromRoute || false);
  const basicOptions = {
    expectedModels: ["List", "GroupingPanel", "Detail", "CaseSearch"],
    targetModel: [ListModel, GroupingPanelModel, DetailModel, CaseSearchModel],
    ...(hookOptions: HookOptions),
  };

  return useModularUIBasic("panel", url, basicOptions);
};
